import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/components/Notifications/Toast';
import { ApiError } from '../utils/apiErrors';
import type { Component, CreateComponentDTO, UpdateComponentDTO } from '@fmeastudio/shared';
import {
  getComponents,
  getComponent,
  createComponent,
  updateComponent,
  deleteComponent,
  getComponentHierarchy,
  getComponentChildren,
  moveComponent,
  uploadThumbnail,
  deleteThumbnail,
  updateComponentPosition,
  updateComponentContainerPosition,
  updateComponentCollapsed,
} from '@/api/components';
import { useProject } from '@/contexts/ProjectContext';
import { logger } from '../utils/logger';

export interface ComponentFilters {
  componentType?: string;
  parentId?: string;
  verificationStatus?: string;
  search?: string;
}

// Get all components with optional filters.
//
// `projectIdOverride` pins the query to a specific project instead of the
// global ProjectContext.activeProject. Single-project surfaces (e.g. Interlock,
// which is hard-pinned to PROJECT_ID='PRO-302') MUST pass it — otherwise the
// query silently follows whatever project the user last selected elsewhere in
// the app, loading the wrong project's components. Omit it to keep the legacy
// activeProject-follows behaviour (the /components page).
export function useComponents(filters?: ComponentFilters, projectIdOverride?: string) {
  const { activeProject } = useProject();
  const projectId = projectIdOverride ?? activeProject?.id;

  return useQuery({
    queryKey: ['components', projectId, filters],
    queryFn: () => {
      if (!projectId) return [];
      return getComponents({ ...filters, projectId });
    },
    enabled: !!projectId,
    // Longer staleTime to prevent diagram re-renders on refetch
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get single component by ID
export function useComponent(id: string) {
  return useQuery({
    queryKey: ['components', id],
    queryFn: () => getComponent(id),
    enabled: !!id,
  });
}

// Get component hierarchy (tree structure)
export function useComponentHierarchy(rootId?: string) {
  const { activeProject } = useProject();

  return useQuery({
    queryKey: ['components', 'hierarchy', activeProject?.id, rootId],
    queryFn: () => {
      if (!activeProject) return [];
      return getComponentHierarchy(rootId, activeProject.id);
    },
    enabled: !!activeProject,
  });
}

// Get children of a specific component
export function useComponentChildren(parentId: string) {
  return useQuery({
    queryKey: ['components', parentId, 'children'],
    queryFn: () => getComponentChildren(parentId),
    enabled: !!parentId,
  });
}

// Create new component
// `projectIdOverride` pins creates to a specific project instead of the global
// ProjectContext.activeProject — needed by single-project surfaces (Interlock
// System view) that never set activeProject, where Clone/Add otherwise silently
// failed with "No active project" or created into the wrong project.
export function useCreateComponent(projectIdOverride?: string) {
  const queryClient = useQueryClient();
  const { activeProject } = useProject();

  return useMutation({
    mutationFn: (data: CreateComponentDTO) => {
      const projectId = projectIdOverride ?? activeProject?.id;
      if (!projectId) throw new Error('No active project');
      return createComponent({ ...data, projectId });
    },
    onSuccess: () => {
      // Invalidate all component queries
      queryClient.invalidateQueries({ queryKey: ['components'] });
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to create component');
    },
  });
}

// Update existing component
export function useUpdateComponent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateComponentDTO }) =>
      updateComponent(id, data),
    onSuccess: (_, variables) => {
      // BUG-FIX: Optimistically update cache with the changed data immediately
      // This ensures changes like parentLayoutConfig persist through state changes
      // (e.g., collapse/expand) that trigger useMemo recalculations before refetch completes
      queryClient.setQueriesData<Component[]>({ queryKey: ['components'] }, (old) => {
        if (!old) return old;
        return old.map((comp) =>
          comp.id === variables.id ? { ...comp, ...variables.data } : comp
        );
      });
      // Also invalidate to ensure fresh data from server eventually
      queryClient.invalidateQueries({ queryKey: ['components', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['components'] });
    },
    onError: (error: Error, variables) => {
      // Check if it's a 404 error (component doesn't exist). Prefer the typed
      // ApiError.status (set by throwIfNotOk in api/components.ts) over a
      // message-substring sniff, which broke once updateComponent started
      // surfacing the server's real error text (e.g. optimistic-lock 409s)
      // instead of a hardcoded "(404)" string.
      const is404 =
        (error instanceof ApiError && error.status === 404) ||
        error.message?.includes('404') ||
        error.message?.includes('Not Found');

      if (is404) {
        // Component doesn't exist - invalidate cache to remove stale data
        logger.warn(`Component ${variables.id} not found, invalidating cache`);
        queryClient.invalidateQueries({ queryKey: ['components'] });
        showToast.error('This component no longer exists. Refreshing list...');
      } else if (error instanceof ApiError && error.status === 409) {
        // Optimistic-lock conflict (VERSION_CONFLICT) — someone else saved
        // over this component. Don't apply the stale local edit; resync from
        // the server instead of leaving a mismatched cache.
        queryClient.invalidateQueries({ queryKey: ['components', variables.id] });
        queryClient.invalidateQueries({ queryKey: ['components'] });
        showToast.error(error.message || 'This component was changed by someone else. Reloaded the latest version.');
      } else {
        showToast.error(error.message || 'Failed to update component');
      }
    },
  });
}

// Delete component
export function useDeleteComponent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteComponent(id),
    onSuccess: (_, id) => {
      // Remove from cache and invalidate lists
      queryClient.removeQueries({ queryKey: ['components', id] });
      queryClient.invalidateQueries({ queryKey: ['components'] });
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to delete component');
    },
  });
}

// Move component to new parent
export function useMoveComponent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newParentId }: { id: string; newParentId: string | null }) =>
      moveComponent(id, newParentId),
    onSuccess: () => {
      // Invalidate all component queries since hierarchy changed
      queryClient.invalidateQueries({ queryKey: ['components'] });
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to move component');
    },
  });
}

// Upload thumbnail for component
export function useUploadThumbnail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ componentId, file }: { componentId: string; file: File }) =>
      uploadThumbnail(componentId, file),
    onSuccess: (_, variables) => {
      // Invalidate specific component and all lists
      queryClient.invalidateQueries({ queryKey: ['components', variables.componentId] });
      queryClient.invalidateQueries({ queryKey: ['components'] });
      showToast.success('Thumbnail uploaded');
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to upload thumbnail');
    },
  });
}

// Delete thumbnail from component
export function useDeleteThumbnail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (componentId: string) => deleteThumbnail(componentId),
    onSuccess: (_, componentId) => {
      // Invalidate specific component and all lists
      queryClient.invalidateQueries({ queryKey: ['components', componentId] });
      queryClient.invalidateQueries({ queryKey: ['components'] });
      showToast.success('Thumbnail removed');
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to remove thumbnail');
    },
  });
}

// Update component diagram position (SoW-297)
export function useUpdateComponentPosition() {
  const queryClient = useQueryClient();
  const { activeProject } = useProject();

  return useMutation({
    mutationFn: ({
      id,
      diagramX,
      diagramY,
      diagramWidth,
      diagramHeight,
    }: {
      id: string;
      diagramX: number;
      diagramY: number;
      diagramWidth?: number;
      diagramHeight?: number;
    }) => updateComponentPosition(id, { diagramX, diagramY, diagramWidth, diagramHeight }),
    onSuccess: (_, variables) => {
      // Update all component cache entries that might contain this component
      // This handles the case where components are queried with different filter combinations
      queryClient.setQueriesData<Component[]>({ queryKey: ['components'] }, (old) => {
        if (!old) return old;
        return old.map((comp) =>
          comp.id === variables.id
            ? {
                ...comp,
                diagramX: variables.diagramX,
                diagramY: variables.diagramY,
                // Also update dimensions if provided (for resize operations)
                ...(variables.diagramWidth !== undefined && {
                  diagramWidth: variables.diagramWidth,
                }),
                ...(variables.diagramHeight !== undefined && {
                  diagramHeight: variables.diagramHeight,
                }),
              }
            : comp
        );
      });
    },
    onError: (error: Error) => {
      // Only log, don't show toast for position updates (too noisy)
      logger.error('Failed to update component position:', error.message);
    },
  });
}

// Update component container diagram position (used when viewing as container in drilled-down view)
export function useUpdateComponentContainerPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      containerDiagramX,
      containerDiagramY,
      containerDiagramWidth,
      containerDiagramHeight,
    }: {
      id: string;
      containerDiagramX: number;
      containerDiagramY: number;
      containerDiagramWidth?: number;
      containerDiagramHeight?: number;
    }) =>
      updateComponentContainerPosition(id, {
        containerDiagramX,
        containerDiagramY,
        containerDiagramWidth,
        containerDiagramHeight,
      }),
    onSuccess: (_, variables) => {
      queryClient.setQueriesData<Component[]>({ queryKey: ['components'] }, (old) => {
        if (!old) return old;
        return old.map((comp) =>
          comp.id === variables.id
            ? {
                ...comp,
                containerDiagramX: variables.containerDiagramX,
                containerDiagramY: variables.containerDiagramY,
                ...(variables.containerDiagramWidth !== undefined && {
                  containerDiagramWidth: variables.containerDiagramWidth,
                }),
                ...(variables.containerDiagramHeight !== undefined && {
                  containerDiagramHeight: variables.containerDiagramHeight,
                }),
              }
            : comp
        );
      });
    },
    onError: (error: Error) => {
      logger.error('Failed to update component container position:', error.message);
    },
  });
}

// Update component diagram collapsed state (SoW-297-003)
export function useUpdateComponentCollapsed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isCollapsed }: { id: string; isCollapsed: boolean }) =>
      updateComponentCollapsed(id, isCollapsed),
    // Optimistic update - immediately update the cache before the API call
    onMutate: async (variables) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['components'] });

      // Snapshot the previous value
      const previousComponents = queryClient.getQueriesData<Component[]>({
        queryKey: ['components'],
      });

      // Optimistically update to the new value
      queryClient.setQueriesData<Component[]>({ queryKey: ['components'] }, (old) => {
        if (!old) return old;
        return old.map((comp) =>
          comp.id === variables.id ? { ...comp, diagramCollapsed: variables.isCollapsed } : comp
        );
      });

      // Return context with previous value for rollback
      return { previousComponents };
    },
    onSuccess: () => {
      // Cache is already updated optimistically, no need to update again
    },
    onError: (error: Error, variables, context) => {
      logger.error(`[useUpdateComponentCollapsed] FAILED for ${variables.id}: ${error.message}`);
      // Rollback the optimistic update on error
      if (context?.previousComponents) {
        context.previousComponents.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}
